import React, { useState, useRef, useEffect } from 'react';
import { X, FileText, Info, CheckCircle } from 'lucide-react';
import { useModal } from '../hooks/useModal';

/**
 * TermsAndConditionsPopup
 * 
 * Mostra un popup con i termini e condizioni che l'utente deve leggere completamente
 * prima di poter accettare. Richiede lo scroll fino al fondo prima dell'accettazione.
 * 
 * Props:
 * - settings: oggetto con i testi dei termini
 * - onClose: callback quando chiude il popup
 * - onAccept: callback quando accetta i termini
 */
const TermsAndConditionsPopup = ({
  settings,
  onClose,
  onAccept,
  title = 'Termini e Condizioni',
  subtitle = 'Leggi attentamente prima di procedere',
  buttonLabel = 'Accetto i Termini e Condizioni',
}) => {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const contentRef = useRef(null);

  const checkIfScrollNeeded = () => {
    const el = contentRef.current;
    if (!el) return;
    // Se il contenuto è più corto (o appena più lungo) dell'area visibile,
    // non c'è nulla da scorrere: sblocchiamo subito il pulsante.
    if (el.scrollHeight - el.clientHeight <= 20) {
      setScrolledToBottom(true);
    }
  };

  useEffect(() => {
    // Controlla al montaggio e ogni volta che cambia il testo dei termini,
    // così se il testo è breve il pulsante non resta bloccato per sempre.
    checkIfScrollNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.terms?.termsText, settings?.terms?.additionalInfo]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      setScrolledToBottom(true);
    }
  };

  // Audit 2026-09-20, punto #11: accessibilità. Questo popup si apre spesso
  // ANNIDATO dentro BookingModal (che ha già il proprio useModal attivo) —
  // lo stack condiviso in useModal.js fa sì che Escape chiuda solo questo
  // popup, non anche il BookingModal sottostante.
  const modalRef = useModal(onClose);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="terms-popup-title" className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 id="terms-popup-title" className="text-2xl font-bold text-gray-800">{title}</h2>
                <p className="text-sm text-gray-600">{subtitle}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content con scroll */}
        <div className="p-6 overflow-y-auto flex-1" onScroll={handleScroll} ref={contentRef}>
          <div className="prose max-w-none">
            <div className="bg-gray-50 rounded-lg p-6 mb-4 border border-gray-200">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {settings.terms?.termsText || 'Termini e condizioni non disponibili.'}
              </p>
            </div>
            {settings.terms?.additionalInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">{settings.terms.additionalInfo}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer con pulsanti */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          {!scrolledToBottom && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800">
                Scorri fino in fondo per abilitare il pulsante di accettazione
              </p>
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              onClick={onAccept}
              disabled={!scrolledToBottom}
              className={`flex-1 py-3 rounded-lg font-medium transition ${
                scrolledToBottom
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {buttonLabel}
              </span>
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              Annulla
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPopup;
